import type { Maybe } from '@adornis/base/utilTypes.js';
import type { RouteOptimizationResult } from '../entities/RouteOptimizationResult.js';
import {
  FE_COST_PER_DAY,
  OVERTIME_COST_PER_HOUR,
  PARTNER_FE_COST_PER_TICKET,
  PUDO_VISIT_COST,
  VEHICLE_COST_PER_DAY,
  VEHICLE_COST_PER_KILOMETER,
} from '../shared/pricing.js';
import { dedupeDepotStops } from './dedupeDepotStops.js';
import { extractIDOnlyFromFEKey } from './extractIDOnlyFromFEKey.js';
import { getTotalServiceTime } from './getTotalServiceTime.js';
import { getTotalTravelTime } from './getTotalTravelTime.js';
import { getWorkingTimeMinutes } from './getWorkingTimeMinutes.js';
import { isInterventionStop } from './isInterventionStop.js';
import { VEHICLE_MAX_ROUTE_DURATION_MINUTES } from './optimizeRoute.js';

// this is not the assignable working time but the full working time with break (9 - 17)
export const FULL_DAY_MINUTES = 8 * 60;

type ProfitabilityEngineer = {
  id?: string | null;
  name?: string | null;
  theoreticalStartTime?: string | Date | null;
  theoreticalEndTime?: string | Date | null;
  startTime?: string | Date | null;
  endTime?: string | Date | null;
};

export function getEngineerProfitabilitySummary(
  engineer: ProfitabilityEngineer,
  routeRevenueByEngineer: ReadonlyMap<
    string,
    { ticketCount: number; ticketRevenue: number; area: string; travelDistanceKm: number }
  >,
  selectedPlanning: Maybe<RouteOptimizationResult>,
) {
  const idKey = engineer.id ? extractIDOnlyFromFEKey(engineer.id) : undefined;
  const nameKey = engineer.name ? extractIDOnlyFromFEKey(engineer.name) : undefined;
  const routeStats =
    (idKey ? routeRevenueByEngineer.get(idKey) : undefined) ??
    (nameKey ? routeRevenueByEngineer.get(nameKey) : undefined);
  const ticketRevenue = routeStats?.ticketRevenue ?? 0;
  const ticketCount = routeStats?.ticketCount ?? 0;
  const travelDistanceKm = routeStats?.travelDistanceKm ?? 0;
  const route = selectedPlanning?.routes.find(r => r.vehicleId === engineer.id);

  const isPartnerFieldEngineer =
    !selectedPlanning?.fieldEngineersAvailable?.some(
      fe =>
        (idKey && extractIDOnlyFromFEKey(fe.id) === idKey) || (nameKey && extractIDOnlyFromFEKey(fe.name) === nameKey),
    ) &&
    selectedPlanning?.partnerFieldEngineersAvailable?.some(
      fe =>
        (idKey && extractIDOnlyFromFEKey(fe.id) === idKey) || (nameKey && extractIDOnlyFromFEKey(fe.name) === nameKey),
    );

  const scheduleMinutes = getWorkingTimeMinutes(
    engineer.theoreticalStartTime ?? engineer.startTime,
    engineer.theoreticalEndTime ?? engineer.endTime,
    false,
  );
  const nonInterventionStops = route?.stops?.filter(stop => !isInterventionStop(stop));
  const routeMinutes = route ? getTotalTravelTime(route.stops) + getTotalServiceTime(nonInterventionStops) : 0;

  let feCostFactor: number;
  if (scheduleMinutes !== null) {
    feCostFactor = Math.min(scheduleMinutes, FULL_DAY_MINUTES) / FULL_DAY_MINUTES;
  } else if (route) {
    feCostFactor = 1;
  } else {
    feCostFactor = 0;
  }

  let baseFeCost: number;
  let vehicleCost: number;
  let overtimeCost: number;
  let pudoCost: number;
  let uniqueDepotCount: number;

  if (isPartnerFieldEngineer) {
    baseFeCost = ticketCount * PARTNER_FE_COST_PER_TICKET;
    feCostFactor = 1;
    vehicleCost = 0;
    overtimeCost = 0;
    pudoCost = 0;
    uniqueDepotCount = 0;
  } else {
    baseFeCost = FE_COST_PER_DAY * feCostFactor;
    vehicleCost = (route ? VEHICLE_COST_PER_DAY : 0) + travelDistanceKm * VEHICLE_COST_PER_KILOMETER;

    const overtimeMinutes = Math.max(0, routeMinutes - VEHICLE_MAX_ROUTE_DURATION_MINUTES);
    overtimeCost = (overtimeMinutes / 60) * OVERTIME_COST_PER_HOUR;

    uniqueDepotCount = route ? dedupeDepotStops(route.stops?.filter(stop => stop.type === 'depot') ?? []).length : 0;
    pudoCost = uniqueDepotCount * PUDO_VISIT_COST;
  }

  const adjustedFeCost = baseFeCost + vehicleCost + overtimeCost + pudoCost;

  return {
    ticketCount,
    ticketRevenue,
    area: routeStats?.area ?? 'Unknown',
    travelDistanceKm,
    scheduleMinutes,
    routeMinutes,
    isPartnerFieldEngineer,
    baseFeCost,
    feCostFactor,
    vehicleCost,
    overtimeCost,
    pudoCost,
    uniqueDepotCount,
    adjustedFeCost,
    profitDelta: ticketRevenue - adjustedFeCost,
  };
}
